import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useNode, useEditor } from '@craftjs/core';

import { EDITOR_RESOLVERS } from '@common/constants';
import { calcNodesWidth } from '@common/helpers/helpers';

import { createNewCellOnTableDrop } from './utils';

const TableHeader = ({ children, elementProps }) => {
  const {
    connectors: { connect },
    actions: { setCustom },
    id,
    parentId,
    childNodes,
  } = useNode(({ data }) => ({
    parentId: data?.parent,
    childNodes: data?.nodes,
  }));

  const { query, actions, allNodes } = useEditor((state) => ({
    allNodes: state.nodes,
  }));

  const { t } = useTranslation();
  const ref = useRef(null);

  const tableNode = parentId && query?.node(parentId)?.get();

  const totalWidth = tableNode?.data?.props?.showHeader
    ? calcNodesWidth(childNodes, allNodes)
    : 0;
  const hasOverflow = totalWidth > 100;

  useEffect(() => {
    if (ref?.current && connect) {
      connect(ref.current);
    }
  }, [connect]);

  useEffect(() => {
    actions.history.ignore(() => {
      setTimeout(() => {
        actions.setProp(parentId, (props) => {
          props.isValid = !hasOverflow;
        });
      }, 0);
    });
  }, [hasOverflow]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('table_header');
      });
    });
  }, [setCustom, t]);

  useEffect(() => {
    createNewCellOnTableDrop(id, query, actions, {
      isHeader: true,
      isFixed: elementProps?.isFixed,
    });
  }, []);

  return (
    tableNode?.data?.props?.showHeader && (
      <thead>
        <tr
          ref={(ref) => connect(ref)}
          style={{ outline: hasOverflow ? '1px solid red' : 'none' }}
        >
          {children}
        </tr>
        {hasOverflow && (
          <tr>
            <td colSpan={100} style={{ color: 'red', fontSize: '12px' }}>
              ⚠️ {`${t('total_width')}: ${totalWidth}%`}
            </td>
          </tr>
        )}
      </thead>
    )
  );
};

const TableHeaderConfigPanel = () => {
  return <div></div>;
};

export const TableHeaderDefaultProps = {
  children: null,
};

TableHeader.defaultProps = TableHeaderDefaultProps;

TableHeader.propTypes = {
  children: PropTypes.node,
};

TableHeader.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.TABLE_HEADER,
  name: EDITOR_RESOLVERS.COMMON.TABLE_HEADER,
  props: TableHeaderDefaultProps,
  related: { settings: TableHeaderConfigPanel },
  rules: {
    isDeletable: () => false,
    canMoveIn: (incoming) => incoming.data?.displayName === 'TableCell',
  },
};

export default TableHeader;
